import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { applyTheme } from "../../utils/themeLoader";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const DEFAULT_THEME = {
  fontFamily: "'Inter', sans-serif",
  fontSize: {
    h1: "28px",
    h2: "24px",
    h3: "20px",
    button: "16px",
    body: "15px",
    label: "14px",
    icon: "24px",
  },
  fontWeight: {
    semibold: 600,
    medium: 500,
    normal: 400,
  },
  colors: {
    primary: "#2563eb",
    primaryGradientStart: "#1e40af",
    primaryGradientEnd: "#3b82f6",
    secondary: "#10b981",
    danger: "#ef4444",
    warning: "#f59e0b",
    textPrimary: "#1a1a1a",
    textSecondary: "#6b7280",
  },
  mobile: {
    fontFamily: "system",
    colors: {
      primary: "#2f78b8",
      accent: "#c95852",
      background: "#f1f3f6",
      surface: "#ffffff",
      card: "#e6ebf1",
      textPrimary: "#0f172a",
      textSecondary: "#4b5563",
      border: "#d5dbe3",
    },
    typography: {
      body: "13px",
      secondary: "12px",
      sectionTitle: "16px",
      heroTitle: "18px",
    },
    controls: {
      buttonVerticalPadding: 8,
      buttonRadius: 8,
      cardRadiusMin: 8,
      cardRadiusMax: 10,
      inputVerticalPadding: 8,
    },
    fontWeight: {
      headingMin: 600,
      headingMax: 700,
    },
  },
};

const DEFAULT_MOBILE_APP_SETTINGS = {
  latestVersion: "1.0.0",
  minimumSupportedVersion: "1.0.0",
  forceUpdateBelowMinimum: true,
  appStoreUrl: "",
  playStoreUrl: "",
};

const mergeTheme = (incoming = {}) => ({
  ...DEFAULT_THEME,
  ...incoming,
  fontSize: { ...DEFAULT_THEME.fontSize, ...(incoming.fontSize || {}) },
  fontWeight: { ...DEFAULT_THEME.fontWeight, ...(incoming.fontWeight || {}) },
  colors: { ...DEFAULT_THEME.colors, ...(incoming.colors || {}) },
  mobile: {
    ...DEFAULT_THEME.mobile,
    ...(incoming.mobile || {}),
    typography: {
      ...DEFAULT_THEME.mobile.typography,
      ...((incoming.mobile && incoming.mobile.typography) || {}),
    },
    colors: {
      ...DEFAULT_THEME.mobile.colors,
      ...((incoming.mobile && incoming.mobile.colors) || {}),
    },
    controls: {
      ...DEFAULT_THEME.mobile.controls,
      ...((incoming.mobile && incoming.mobile.controls) || {}),
    },
    fontWeight: {
      ...DEFAULT_THEME.mobile.fontWeight,
      ...((incoming.mobile && incoming.mobile.fontWeight) || {}),
    },
  },
});

const ThemeSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("web");
  const [message, setMessage] = useState({ type: "", text: "" });

  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [mobileApp, setMobileApp] = useState(DEFAULT_MOBILE_APP_SETTINGS);

  useEffect(() => {
    checkPortalAdmin();
    fetchTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPortalAdmin = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      // Check if user has portal admin role (either in primary role or roles array)
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin"); // Redirect to portal admin dashboard, not business dashboard
      }
    } catch (error) {
      navigate("/login");
    }
  };

  const fetchTheme = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.get(
        `${API_BASE_URL}/api/platform-settings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Response returns settings directly, not wrapped
      if (response.data && response.data.themeSettings) {
        setTheme(mergeTheme(response.data.themeSettings));
      }
      if (response.data && response.data.mobileApp) {
        setMobileApp({
          ...DEFAULT_MOBILE_APP_SETTINGS,
          ...response.data.mobileApp,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("ogestyle_token");
      const response = await axios.put(
        `${API_BASE_URL}/api/platform-settings`,
        {
          themeSettings: theme,
          mobileApp,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.message || response.data.settings) {
        setMessage({
          type: "success",
          text: "Theme and mobile app update settings saved successfully!",
        });
        // Apply theme immediately
        applyTheme(theme);
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to save theme settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    applyTheme(theme);
    setMessage({
      type: "success",
      text: "Theme preview applied! Refresh to revert.",
    });
  };

  const handleReset = () => {
    setTheme(DEFAULT_THEME);
    setMobileApp(DEFAULT_MOBILE_APP_SETTINGS);
    setMessage({ type: "success", text: "Reset to default theme values" });
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading theme settings...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h1
          style={{ fontSize: "28px", fontWeight: "700", marginBottom: "8px" }}
        >
          🎨 Theme Settings
        </h1>
        <p style={{ color: "#6b7280", marginBottom: "32px" }}>
          Customize fonts, colors, and typography across the entire platform
        </p>

        {message.text && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              background: message.type === "success" ? "#ecfdf5" : "#fee2e2",
              color: message.type === "success" ? "#059669" : "#dc2626",
              border: `1px solid ${message.type === "success" ? "#10b981" : "#ef4444"}`,
            }}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave}>
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "20px",
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("web")}
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background: activeTab === "web" ? "#2d6da3" : "#ffffff",
                color: activeTab === "web" ? "#ffffff" : "#1f2937",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Web Settings
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("mobile")}
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background:
                  activeTab === "mobile" ? "#2d6da3" : "#ffffff",
                color: activeTab === "mobile" ? "#ffffff" : "#1f2937",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Mobile Settings
            </button>
          </div>

          {activeTab === "web" && (
            <>
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "24px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "20px",
              }}
            >
              📝 Typography
            </h2>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "block",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Font Family
              </label>
              <select
                value={theme.fontFamily}
                onChange={(e) =>
                  setTheme({ ...theme, fontFamily: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                <option value="'Inter', sans-serif">Inter (Default)</option>
                <option value="'Roboto', sans-serif">Roboto</option>
                <option value="'Open Sans', sans-serif">Open Sans</option>
                <option value="'Lato', sans-serif">Lato</option>
                <option value="'Montserrat', sans-serif">Montserrat</option>
                <option value="'Poppins', sans-serif">Poppins</option>
                <option value="'Raleway', sans-serif">Raleway</option>
                <option value="'Nunito', sans-serif">Nunito</option>
                <option value="'Playfair Display', serif">
                  Playfair Display (Serif)
                </option>
                <option value="'Merriweather', serif">
                  Merriweather (Serif)
                </option>
                <option value="'PT Serif', serif">PT Serif</option>
                <option value="'Source Sans Pro', sans-serif">
                  Source Sans Pro
                </option>
                <option value="'Work Sans', sans-serif">Work Sans</option>
                <option value="'Quicksand', sans-serif">Quicksand</option>
                <option value="'Rubik', sans-serif">Rubik</option>
                <option value="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif">
                  System Default
                </option>
              </select>
              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  margin: "4px 0 0",
                }}
              >
                Select a font family for the entire platform
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  H1 Size (Page Titles)
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="20"
                    max="48"
                    value={parseInt(theme.fontSize.h1)}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          h1: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.h1}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, h1: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  H2 Size (Section Headers)
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="16"
                    max="36"
                    value={parseInt(theme.fontSize.h2)}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          h2: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.h2}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, h2: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  H3 Size (Card Titles)
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="14"
                    max="28"
                    value={parseInt(theme.fontSize.h3)}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          h3: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.h3}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, h3: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Button Size
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="12"
                    max="20"
                    value={parseInt(theme.fontSize.button)}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          button: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.button}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, button: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Body Text Size
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="12"
                    max="20"
                    value={parseInt(theme.fontSize.body)}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          body: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.body}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, body: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Menu Icon Size
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <input
                    type="range"
                    min="18"
                    max="36"
                    value={parseInt(theme.fontSize?.icon) || 24}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: {
                          ...theme.fontSize,
                          icon: e.target.value + "px",
                        },
                      })
                    }
                    style={{ flex: 1 }}
                  />
                  <input
                    type="text"
                    value={theme.fontSize.icon}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        fontSize: { ...theme.fontSize, icon: e.target.value },
                      })
                    }
                    style={{
                      width: "60px",
                      padding: "6px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Colors Section */}
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "24px",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "20px",
              }}
            >
              🎨 Colors
            </h2>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Brand Color (Primary)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="color"
                    value={theme.colors.primary}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, primary: e.target.value },
                      })
                    }
                    style={{
                      width: "50px",
                      height: "40px",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                    }}
                  />
                  <input
                    type="text"
                    value={theme.colors.primary}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, primary: e.target.value },
                      })
                    }
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "4px 0 0",
                  }}
                >
                  Main brand color for buttons, links, etc.
                </p>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Gradient Start (Dark Blue)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="color"
                    value={theme.colors.primaryGradientStart}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primaryGradientStart: e.target.value,
                        },
                      })
                    }
                    style={{
                      width: "50px",
                      height: "40px",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                    }}
                  />
                  <input
                    type="text"
                    value={theme.colors.primaryGradientStart}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primaryGradientStart: e.target.value,
                        },
                      })
                    }
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "4px 0 0",
                  }}
                >
                  Darker color for gradient backgrounds
                </p>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Gradient End (Light Blue)
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="color"
                    value={theme.colors.primaryGradientEnd}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primaryGradientEnd: e.target.value,
                        },
                      })
                    }
                    style={{
                      width: "50px",
                      height: "40px",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                    }}
                  />
                  <input
                    type="text"
                    value={theme.colors.primaryGradientEnd}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: {
                          ...theme.colors,
                          primaryGradientEnd: e.target.value,
                        },
                      })
                    }
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "4px 0 0",
                  }}
                >
                  Lighter color for gradient backgrounds
                </p>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Gradient Preview
                </label>
                <div
                  style={{
                    height: "40px",
                    borderRadius: "6px",
                    background: `linear-gradient(135deg, ${theme.colors.primaryGradientStart} 0%, ${theme.colors.primaryGradientEnd} 100%)`,
                    border: "1px solid #d1d5db",
                  }}
                />
                <p
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    margin: "4px 0 0",
                  }}
                >
                  How your brand gradient will look
                </p>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Secondary Color
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="color"
                    value={theme.colors.secondary}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, secondary: e.target.value },
                      })
                    }
                    style={{
                      width: "50px",
                      height: "40px",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: "6px",
                    }}
                  />
                  <input
                    type="text"
                    value={theme.colors.secondary}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, secondary: e.target.value },
                      })
                    }
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  Danger Color
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    type="color"
                    value={theme.colors.danger}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, danger: e.target.value },
                      })
                    }
                    style={{
                      width: "50px",
                      height: "40px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    value={theme.colors.danger}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        colors: { ...theme.colors, danger: e.target.value },
                      })
                    }
                    style={{
                      flex: 1,
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
            </>
          )}

          {activeTab === "mobile" && (
            <>
              <div
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  🔄 Mobile App Update Prompt
                </h2>
                <p style={{ color: "#6b7280", marginTop: 0, marginBottom: "18px" }}>
                  Configure soft and forced update prompts for the mobile app.
                </p>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
                      Latest App Version
                    </label>
                    <input
                      type="text"
                      value={mobileApp.latestVersion}
                      onChange={(e) =>
                        setMobileApp({
                          ...mobileApp,
                          latestVersion: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
                      Minimum Supported Version
                    </label>
                    <input
                      type="text"
                      value={mobileApp.minimumSupportedVersion}
                      onChange={(e) =>
                        setMobileApp({
                          ...mobileApp,
                          minimumSupportedVersion: e.target.value,
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "500" }}>
                    <input
                      type="checkbox"
                      checked={mobileApp.forceUpdateBelowMinimum}
                      onChange={(e) =>
                        setMobileApp({
                          ...mobileApp,
                          forceUpdateBelowMinimum: e.target.checked,
                        })
                      }
                    />
                    Force update when below minimum supported version
                  </label>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
                      Apple App Store URL
                    </label>
                    <input
                      type="url"
                      value={mobileApp.appStoreUrl}
                      onChange={(e) =>
                        setMobileApp({
                          ...mobileApp,
                          appStoreUrl: e.target.value,
                        })
                      }
                      placeholder="https://apps.apple.com/app/id..."
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>
                      Google Play Store URL
                    </label>
                    <input
                      type="url"
                      value={mobileApp.playStoreUrl}
                      onChange={(e) =>
                        setMobileApp({
                          ...mobileApp,
                          playStoreUrl: e.target.value,
                        })
                      }
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginBottom: "10px",
                  }}
                >
                  📱 Mobile Typography & Sizing
                </h2>
                <p style={{ color: "#6b7280", marginTop: 0, marginBottom: "18px" }}>
                  Control compact mobile UI tokens for iOS and Android.
                </p>

                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h3 style={{ margin: "0 0 12px", fontSize: "16px", color: "#0f172a" }}>
                    Mobile Color Palette
                  </h3>
                  <p style={{ margin: "0 0 14px", color: "#64748b", fontSize: "13px" }}>
                    These values control OGESTYLE mobile app background, cards, text, and primary accents.
                  </p>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    {[
                      ["primary", "Primary (Main Blue)"],
                      ["accent", "Accent (Highlight)"] ,
                      ["background", "Background"],
                      ["surface", "Surface"],
                      ["card", "Card"],
                      ["textPrimary", "Text Primary"],
                      ["textSecondary", "Text Secondary"],
                      ["border", "Border"],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label
                          style={{
                            display: "block",
                            fontWeight: "500",
                            marginBottom: "6px",
                            fontSize: "13px",
                          }}
                        >
                          {label}
                        </label>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <input
                            type="color"
                            value={theme.mobile.colors[key]}
                            onChange={(e) =>
                              setTheme({
                                ...theme,
                                mobile: {
                                  ...theme.mobile,
                                  colors: {
                                    ...theme.mobile.colors,
                                    [key]: e.target.value,
                                  },
                                },
                              })
                            }
                            style={{
                              width: "44px",
                              height: "40px",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          />
                          <input
                            type="text"
                            value={theme.mobile.colors[key]}
                            onChange={(e) =>
                              setTheme({
                                ...theme,
                                mobile: {
                                  ...theme.mobile,
                                  colors: {
                                    ...theme.mobile.colors,
                                    [key]: e.target.value,
                                  },
                                },
                              })
                            }
                            style={{
                              flex: 1,
                              padding: "10px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      marginBottom: "8px",
                    }}
                  >
                    Mobile Font Family
                  </label>
                  <select
                    value={theme.mobile.fontFamily}
                    onChange={(e) =>
                      setTheme({
                        ...theme,
                        mobile: { ...theme.mobile, fontFamily: e.target.value },
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      fontSize: "14px",
                      background: "white",
                    }}
                  >
                    <option value="system">System Default (SF Pro / Roboto)</option>
                    <option value="inter">Inter</option>
                    <option value="roboto">Roboto</option>
                  </select>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Body / Label Text</label>
                    <input
                      type="text"
                      value={theme.mobile.typography.body}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            typography: {
                              ...theme.mobile.typography,
                              body: e.target.value,
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Small / Secondary Text</label>
                    <input
                      type="text"
                      value={theme.mobile.typography.secondary}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            typography: {
                              ...theme.mobile.typography,
                              secondary: e.target.value,
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Section Titles</label>
                    <input
                      type="text"
                      value={theme.mobile.typography.sectionTitle}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            typography: {
                              ...theme.mobile.typography,
                              sectionTitle: e.target.value,
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Hero / Page Title</label>
                    <input
                      type="text"
                      value={theme.mobile.typography.heroTitle}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            typography: {
                              ...theme.mobile.typography,
                              heroTitle: e.target.value,
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Button Vertical Padding</label>
                    <input
                      type="number"
                      min="4"
                      max="16"
                      value={theme.mobile.controls.buttonVerticalPadding}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            controls: {
                              ...theme.mobile.controls,
                              buttonVerticalPadding: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Button Radius</label>
                    <input
                      type="number"
                      min="4"
                      max="20"
                      value={theme.mobile.controls.buttonRadius}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            controls: {
                              ...theme.mobile.controls,
                              buttonRadius: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Card Radius Min</label>
                    <input
                      type="number"
                      min="4"
                      max="20"
                      value={theme.mobile.controls.cardRadiusMin}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            controls: {
                              ...theme.mobile.controls,
                              cardRadiusMin: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Card Radius Max</label>
                    <input
                      type="number"
                      min="4"
                      max="24"
                      value={theme.mobile.controls.cardRadiusMax}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            controls: {
                              ...theme.mobile.controls,
                              cardRadiusMax: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Input Vertical Padding</label>
                    <input
                      type="number"
                      min="4"
                      max="16"
                      value={theme.mobile.controls.inputVerticalPadding}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            controls: {
                              ...theme.mobile.controls,
                              inputVerticalPadding: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Heading Weight Min</label>
                    <input
                      type="number"
                      min="100"
                      max="900"
                      step="100"
                      value={theme.mobile.fontWeight.headingMin}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            fontWeight: {
                              ...theme.mobile.fontWeight,
                              headingMin: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: "500", marginBottom: "8px" }}>Heading Weight Max</label>
                    <input
                      type="number"
                      min="100"
                      max="900"
                      step="100"
                      value={theme.mobile.fontWeight.headingMax}
                      onChange={(e) =>
                        setTheme({
                          ...theme,
                          mobile: {
                            ...theme.mobile,
                            fontWeight: {
                              ...theme.mobile.fontWeight,
                              headingMax: Number(e.target.value),
                            },
                          },
                        })
                      }
                      style={{ width: "100%", padding: "10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div
            style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
          >
            <button
              type="button"
              onClick={handleReset}
              style={{
                padding: "12px 24px",
                background: "#f3f4f6",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Reset to Defaults
            </button>
            {activeTab === "web" && (
              <button
                type="button"
                onClick={handlePreview}
                style={{
                  padding: "12px 24px",
                  background: "#2d6da3",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                Preview Theme
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "12px 24px",
                background: saving ? "#9ca3af" : "#10b981",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              {saving ? "Saving..." : "Save Theme Settings"}
            </button>
          </div>
        </form>

        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "#eff6ff",
            border: "1px solid #3b82f6",
            borderRadius: "8px",
            color: "#1e40af",
          }}
        >
          <strong>ℹ️ How it works:</strong>
          <ul style={{ margin: "8px 0 0", paddingLeft: "20px" }}>
            <li>Changes apply instantly to all users on next page load</li>
            <li>
              Preview button applies changes temporarily (refresh to revert)
            </li>
            <li>Save button makes changes permanent in the database</li>
            <li>Use CSS values: 22px, 1.5rem, 'Arial', #2d6da3, etc.</li>
          </ul>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default ThemeSettings;
